import {I18n} from '@lingui/core';
import {useLingui} from '@lingui/react';
import {AllMerchantAccountListItem} from '@zentact/api/src/trpc/routers/merchantAccountRouter';
import {AdminRole, PortalRole} from '@zentact/common';
import {useEffect} from 'react';
import {Controller, UseFormReturn} from 'react-hook-form';
import {z} from 'zod';
import {InputCheckboxList, InputSearchSelect, InputSelect, InputText} from '../../../forms';
import {Label} from '../../label';
import {Typography} from '../../typography';
import {ValidationError} from '../../validation-error';

export const getOrganizationSelectRoleOptions = (i18n: I18n) => [
  {
    id: PortalRole.ORGANIZATION_ADMIN,
    label: i18n._('Organization Admin'),
  },
  {
    id: PortalRole.MERCHANT_ADMIN,
    label: i18n._('Merchant Admin'),
  },
];
export const getTenantSelectRoleOptions = (i18n: I18n) => [
  {
    id: AdminRole.TENANT_ADMIN,
    label: i18n._('Admin'),
  },
  {
    id: AdminRole.SALES_AGENT,
    label: i18n._('Sales Agent'),
  },
  {
    id: AdminRole.FINANCE_MANAGER,
    label: i18n._('Finance Manager'),
  },
  {
    id: AdminRole.DEVELOPER,
    label: i18n._('Developer'),
  },
  {
    id: AdminRole.SUPPORT,
    label: i18n._('Support'),
  },
];

export const getUserFormSchema = (i18n: I18n, selectOrganization?: boolean) =>
  z
    .object({
      organizationId: z.string().nullable().optional(),
      email: z
        .string()
        .min(1, i18n._('Email is required'))
        .max(70, i18n._(`The length of the email address can't exceed 70 characters`))
        .email(i18n._('Email is not valid')),
      name: z
        .string()
        .min(2, i18n._('Name must contain at least 2 character(s)'))
        .max(37, i18n._('Name must contain at most 37 character(s)')),
      role: z.union([
        z.nativeEnum(AdminRole, {
          // biome-ignore lint/style/useNamingConvention: Zod Convention
          required_error: i18n._('Role is required'),
        }),
        z.nativeEnum(PortalRole, {
          // biome-ignore lint/style/useNamingConvention: Zod Convention
          required_error: i18n._('Role is required'),
        }),
      ]),
      // OrganizationUser only
      merchantAccountIds: z.array(z.string()).optional(),
    })
    .superRefine((data, ctx) => {
      if (
        data.role === PortalRole.MERCHANT_ADMIN &&
        (!data.merchantAccountIds || data.merchantAccountIds.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n._('At least 1 merchant account should be selected'),
          path: ['merchantAccountIds'],
        });
      }
      if (selectOrganization && !data.organizationId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n._('Organization is required'),
          path: ['organizationId'],
        });
      }
      return z.NEVER;
    });

export type UserFormData = z.infer<ReturnType<typeof getUserFormSchema>>;

export type UserFormProps = {
  form: UseFormReturn<UserFormData>;
  initialValues?: UserFormData;
  emailReadonly?: boolean;
  mode: 'organizationUser' | 'tenantUser';
  merchantAccountsOptions?: AllMerchantAccountListItem[];
  selectOrganization?: boolean;
  organizationOptions?: {id: string; label: string}[];
};

export const UserForm = ({
  form,
  initialValues,
  emailReadonly,
  mode,
  merchantAccountsOptions,
  selectOrganization = false,
  organizationOptions,
}: UserFormProps) => {
  const {
    register,
    formState: {errors},
    control,
  } = form;

  useEffect(() => {
    if (initialValues) {
      form.setValue('email', initialValues.email);
      form.setValue('name', initialValues.name);
      form.setValue('role', initialValues.role);
    }
  }, [initialValues]);

  const {i18n} = useLingui();
  const role = form.watch('role');
  const selectedOrganization = form.watch('organizationId');

  useEffect(() => {
    form.resetField('merchantAccountIds');
  }, [selectedOrganization]);

  return (
    <div className="flex flex-col gap-3">
      {selectOrganization && organizationOptions && (
        <>
          <Controller
            name="organizationId"
            control={control}
            render={({field}) => (
              <InputSearchSelect
                {...field}
                label={i18n._('Organization')}
                value={field.value ?? ''}
                className=""
                options={organizationOptions}
                placeholder={i18n._('Select Organization')}
              />
            )}
          />
          <ValidationError isVisible={Boolean(errors.organizationId)}>
            {errors.organizationId?.message}
          </ValidationError>
        </>
      )}
      <Label text={i18n._('Name')}>
        <InputText {...register('name', {required: true})} />
        <ValidationError isVisible={Boolean(errors.name)}>{errors.name?.message}</ValidationError>
      </Label>
      <Label text={i18n._('Email')}>
        <InputText
          {...register('email', {required: true})}
          placeholder="example@mail.com"
          readOnly={emailReadonly}
          disabled={emailReadonly}
          className={emailReadonly ? 'bg-gray-100 text-gray-400' : ''}
        />
        <ValidationError isVisible={Boolean(errors.email)}>{errors.email?.message}</ValidationError>
      </Label>
      <Controller
        control={control}
        defaultValue={
          mode === 'organizationUser' ? PortalRole.ORGANIZATION_ADMIN : AdminRole.TENANT_ADMIN
        }
        name="role"
        render={({field}) => (
          <>
            <InputSelect
              label={i18n._('Role')}
              value={field.value}
              options={
                mode === 'organizationUser'
                  ? getOrganizationSelectRoleOptions(i18n)
                  : getTenantSelectRoleOptions(i18n)
              }
              onChange={value => {
                field.onChange(value);
              }}
              placeholder={i18n._('Role')}
            />
            <ValidationError isVisible={Boolean(errors.role)}>
              {errors.role?.message}
            </ValidationError>
            <div className="p-3 mt-2 border border-gray-100 rounded-md bg-gray-50">
              {field.value === PortalRole.ORGANIZATION_ADMIN && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">
                    {i18n._('Organization Admin')}
                  </h4>
                  <p className="text-xs leading-5 text-gray-600">
                    {i18n._('An Organization Admin can perform all actions for the organization:')}
                  </p>
                  <ul className="ml-5 text-xs leading-5 text-gray-600 list-disc">
                    <li>{i18n._('Manage all merchant accounts')}</li>
                    <li>{i18n._('Configure payment settings')}</li>
                    <li>{i18n._('View and manage all transactions')}</li>
                    <li>{i18n._('Generate statements and reports')}</li>
                    <li>{i18n._('Manage organization users and permissions')}</li>
                  </ul>
                </div>
              )}

              {field.value === PortalRole.MERCHANT_ADMIN && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">{i18n._('Merchant Admin')}</h4>
                  <p className="text-xs leading-5 text-gray-600">
                    {i18n._('A Merchant Admin can manage assigned merchant accounts:')}
                  </p>
                  <ul className="ml-5 text-xs leading-5 text-gray-600 list-disc">
                    <li>{i18n._('Process payments for assigned merchants')}</li>
                    <li>{i18n._('Configure merchant-specific settings')}</li>
                    <li>{i18n._('View and manage merchant transactions')}</li>
                    <li>{i18n._('Access merchant reports and statements')}</li>
                  </ul>
                  <p className="mt-2 text-xs italic leading-5 text-gray-600">
                    {i18n._(
                      'Note: Merchant Admins cannot manage users and can only see their assigned merchants.'
                    )}
                  </p>
                </div>
              )}

              {field.value === AdminRole.TENANT_ADMIN && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">{i18n._('Admin')}</h4>
                  <p className="text-xs leading-5 text-gray-600">
                    {i18n._('Full administrative access to all tenant functions:')}
                  </p>
                  <ul className="ml-5 text-xs leading-5 text-gray-600 list-disc">
                    <li>{i18n._('Manage all organizations and merchants')}</li>
                    <li>{i18n._('Configure platform-wide settings')}</li>
                    <li>{i18n._('Access all financial data and reports')}</li>
                    <li>{i18n._('Manage all users and permissions')}</li>
                    <li>{i18n._('Configure API keys and developer settings')}</li>
                    <li>{i18n._('Manage terminal devices')}</li>
                  </ul>
                </div>
              )}

              {field.value === AdminRole.SALES_AGENT && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">{i18n._('Sales Agent')}</h4>
                  <p className="text-xs leading-5 text-gray-600">
                    {i18n._('Focused on customer management and sales operations:')}
                  </p>
                  <ul className="ml-5 text-xs leading-5 text-gray-600 list-disc">
                    <li>{i18n._('Create and manage organizations')}</li>
                    <li>{i18n._('Set up new merchant accounts')}</li>
                    <li>{i18n._('Configure terminal devices')}</li>
                    <li>{i18n._('Access customer reporting data')}</li>
                    <li>{i18n._('View transaction histories')}</li>
                  </ul>
                </div>
              )}

              {field.value === AdminRole.FINANCE_MANAGER && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">{i18n._('Finance Manager')}</h4>
                  <p className="text-xs leading-5 text-gray-600">
                    {i18n._('Focused on financial operations and reporting:')}
                  </p>
                  <ul className="ml-5 text-xs leading-5 text-gray-600 list-disc">
                    <li>{i18n._('Manage all financial operations')}</li>
                    <li>{i18n._('Review and process transfers')}</li>
                    <li>{i18n._('Configure fee structures')}</li>
                    <li>{i18n._('Generate financial reports')}</li>
                    <li>{i18n._('Manage customer financial settings')}</li>
                    <li>{i18n._('Access comprehensive financial data')}</li>
                  </ul>
                </div>
              )}

              {field.value === AdminRole.DEVELOPER && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">{i18n._('Developer')}</h4>
                  <p className="text-xs leading-5 text-gray-600">
                    {i18n._('Focused on technical configuration and integration:')}
                  </p>
                  <ul className="ml-5 text-xs leading-5 text-gray-600 list-disc">
                    <li>{i18n._('Manage API keys and authentication')}</li>
                    <li>{i18n._('Configure webhook endpoints')}</li>
                    <li>{i18n._('Set up integration settings')}</li>
                    <li>{i18n._('Manage fee configurations')}</li>
                    <li>{i18n._('Configure terminal devices')}</li>
                    <li>{i18n._('Access customer data for testing')}</li>
                  </ul>
                </div>
              )}

              {field.value === AdminRole.SUPPORT && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">{i18n._('Support')}</h4>
                  <p className="text-xs leading-5 text-gray-600">
                    {i18n._('Focused on customer service and issue resolution:')}
                  </p>
                  <ul className="ml-5 text-xs leading-5 text-gray-600 list-disc">
                    <li>{i18n._('View and manage customer accounts')}</li>
                    <li>{i18n._('Assist with transaction issues')}</li>
                    <li>{i18n._('View terminal configurations')}</li>
                    <li>{i18n._('Access customer payment histories')}</li>
                    <li>{i18n._('Help resolve payment disputes')}</li>
                  </ul>
                </div>
              )}
            </div>
          </>
        )}
      />

      {mode === 'organizationUser' &&
        role === PortalRole.MERCHANT_ADMIN &&
        merchantAccountsOptions && (
          <div>
            <Typography variant="header-form-section" className="mb-3 font-normal">
              {i18n._('Merchant Visibility')}
            </Typography>

            <div>
              <Controller
                name="merchantAccountIds"
                control={control}
                render={({field}) => (
                  <InputCheckboxList
                    options={
                      selectOrganization
                        ? merchantAccountsOptions.filter(
                            account => account.organizationId === selectedOrganization
                          )
                        : merchantAccountsOptions
                    }
                    getLabel={merchant => merchant.businessName}
                    getOptionId={merchant => merchant.id}
                    value={field.value}
                    onChange={field.onChange}
                    filterable
                    searchPlaceholder={i18n._('Merchant Account')}
                    className="mb-2"
                  >
                    <ValidationError isVisible={Boolean(errors.merchantAccountIds)}>
                      {errors.merchantAccountIds?.message}
                    </ValidationError>
                  </InputCheckboxList>
                )}
              />
            </div>
          </div>
        )}
    </div>
  );
};
